import { faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';
import { Group, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { Button, ButtonValid, FullScreenModal, Icon, Pill } from '@feathr/components';

import BannersnackEditorModal from '../BannersnackEditorModal';
import CreativeForm from './CreativeForm';
import { useCreativeModal } from './CreativeModal.hooks';
import { getCreativeTypeLabel, isBannersnackCreative } from './CreativeModal.utils';
import PreviewPanel from './PreviewPanel';
import type { IThumbnailCarouselItem } from './ThumbnailCarousel';
import ThumbnailCarousel, { DEFAULT_VISIBLE_COUNT } from './ThumbnailCarousel';

import * as styles from './CreativeModal.css';

export interface ICreativeModalProps {
  creative?: DisplayCreative;
  /** Campaign ID, used to filter CTA options by campaign objective. */
  campaignId?: string;
  /** Array of thumbnail items to display in the carousel. */
  thumbnailItems?: IThumbnailCarouselItem[];
  opened: boolean;
  /** Callback when closing without saving (X button or Cancel). Discards dirty changes automatically. */
  onClose: () => void;
  /** Callback when "Save and close" is clicked. If not provided, just closes. */
  onSave?: () => void | Promise<void>;
  /** Callback when a thumbnail is selected in the carousel. */
  onSelectCreative?: (id: string) => void;
  /**
   * Fields to skip validation for.
   * Useful when a field will be set during the save process (e.g., ad_video_id).
   */
  skipValidationFields?: string[];
  /** Whether the modal is currently saving */
  isSaving?: boolean;
  /**
   * Optional external handler for "Edit in Bannersnack" button.
   * If provided, this is called instead of opening the internal Bannersnack editor.
   * Use this when you need custom Bannersnack editor behavior (e.g., syncing state).
   */
  onEditInBannersnack?: () => void;
}

function CreativeModal({
  creative,
  campaignId,
  thumbnailItems,
  opened,
  onClose,
  onSave,
  onSelectCreative,
  skipValidationFields = [],
  isSaving = false,
  onEditInBannersnack,
}: Readonly<ICreativeModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const {
    handleCancel,
    handleSave,
    handleCloseBannersnackEditor,
    validationErrors,
    bannersnackEditHandler,
    shouldRenderInternalBannersnackEditor,
  } = useCreativeModal({
    creative,
    onClose,
    onSave,
    skipValidationFields,
    onEditInBannersnack,
  });

  const isBannersnack = isBannersnackCreative(creative);

  const title = (
    <Group gap={theme.spacing['2']}>
      <span>{t('Preview creative')}</span>
      <Pill truncate={false}>{getCreativeTypeLabel(creative, t)}</Pill>
    </Group>
  );

  const leftActions =
    thumbnailItems && onSelectCreative ? (
      <div className={styles.carouselWrapper}>
        <ThumbnailCarousel
          items={thumbnailItems}
          onSelect={onSelectCreative}
          selectedId={creative?.id ?? ''}
          visibleCount={Math.min(thumbnailItems.length, DEFAULT_VISIBLE_COUNT)}
        />
      </div>
    ) : undefined;

  const rightActions = (
    <>
      {onSave && (
        <Button name={'creative-modal-cancel'} onClick={handleCancel}>
          {t('Cancel')}
        </Button>
      )}
      <ButtonValid
        errors={validationErrors}
        isLoading={isSaving}
        name={'creative-modal-save'}
        onClick={handleSave}
        prefix={<Icon icon={faFloppyDisk} />}
        type={'primary'}
      >
        {t('Save and close')}
      </ButtonValid>
    </>
  );

  if (shouldRenderInternalBannersnackEditor && creative) {
    return (
      <BannersnackEditorModal
        creative={creative}
        doneLabel={t('Done')}
        onCancel={handleCancel}
        onDone={handleCloseBannersnackEditor}
        opened={opened}
      />
    );
  }

  return (
    <FullScreenModal
      className={styles.modal}
      closeOnEscape={false}
      leftActions={leftActions}
      onClose={handleCancel}
      opened={opened}
      rightActions={rightActions}
      theme={'flush'}
      title={title}
    >
      <div className={styles.body}>
        <div className={styles.previewArea}>
          <PreviewPanel creative={creative} />
        </div>
        <div className={styles.sidebar}>
          {creative && (
            <CreativeForm
              campaignId={campaignId}
              creative={creative}
              onEditInBannersnack={isBannersnack ? bannersnackEditHandler : undefined}
            />
          )}
        </div>
      </div>
    </FullScreenModal>
  );
}

export default observer(CreativeModal);
